import { useEffect, useState } from "react";
import confetti from "canvas-confetti";
import { findCrossedMilestone } from "@/lib/milestones";

const STORAGE_KEY = "mt_admin_last_seen_count";

const fireConfetti = () => {
  // Two staggered bursts from bottom-left & bottom-right for a "rising celebration" feel.
  const colors = ["#D4774A", "#8AA28A", "#F5EDE3", "#3C3A36"]; // brand: terracotta, sage, sand, charcoal
  const defaults = {
    spread: 70,
    startVelocity: 55,
    ticks: 200,
    gravity: 0.9,
    decay: 0.92,
    scalar: 1.1,
    colors,
  };
  confetti({ ...defaults, particleCount: 80, angle: 60, origin: { x: 0.05, y: 0.9 } });
  confetti({ ...defaults, particleCount: 80, angle: 120, origin: { x: 0.95, y: 0.9 } });
  setTimeout(() => {
    confetti({ ...defaults, particleCount: 120, angle: 90, origin: { x: 0.5, y: 0.7 } });
  }, 250);
};

/**
 * Detects when the subscriber count crosses a new milestone since the admin
 * last opened the panel and fires:
 *   - a confetti burst (canvas-confetti)
 *   - returns the crossed milestone so the dashboard can render a banner
 *
 * Behaviour:
 *   - On first ever load: silently records `count` (no celebration for past wins).
 *   - On `count == null/undefined`: does nothing (stats still loading).
 *   - Dismissing the banner clears it from state but does NOT reset the storage,
 *     so the same milestone won't trigger again on reload.
 */
export const useMilestoneCelebration = (count) => {
  const [celebrating, setCelebrating] = useState(null);

  useEffect(() => {
    if (count == null) return;
    let prev;
    try {
      const raw = window.localStorage.getItem(STORAGE_KEY);
      prev = raw == null ? null : parseInt(raw, 10);
    } catch (err) {
      console.warn("[milestone] localStorage read failed:", err);
      return;
    }

    // First ever visit: just remember and move on (don't celebrate retroactively).
    if (prev === null || Number.isNaN(prev)) {
      try { window.localStorage.setItem(STORAGE_KEY, String(count)); } catch (_) { /* ignore */ }
      return;
    }

    const crossed = findCrossedMilestone(prev, count);
    if (crossed) {
      setCelebrating(crossed);
      fireConfetti();
    }

    // Always advance the stored counter to the latest known value.
    try { window.localStorage.setItem(STORAGE_KEY, String(count)); } catch (_) { /* ignore */ }
  }, [count]);

  const dismiss = () => setCelebrating(null);
  return { celebrating, dismiss };
};

export default useMilestoneCelebration;
